import { doc, getDoc, setDoc, updateDoc, Timestamp, deleteField } from 'firebase/firestore';
import { db } from '../firebase/config';

export interface Announcement {
  id: string;
  content: string;
  createdAt: Date;
  updatedAt: Date;
  isActive: boolean;
  title?: string; // 제목 (선택사항)
}

const ANNOUNCEMENT_COLLECTION = 'AllUserDoc';
const ANNOUNCEMENT_DOC_ID = 'announcements';

/** announcements 필드를 배열로 정규화 (V2 객체 { [id]: Announcement } 또는 레거시 배열 지원) */
function announcementsToArray(raw: any): any[] {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'object') return Object.values(raw);
  return [];
}

function toAnnouncement(item: any): Announcement {
  return {
    id: item.id || `announcement-${Date.now()}`,
    content: item.content || '',
    title: item.title,
    createdAt: item.createdAt?.toDate?.() || new Date(),
    updatedAt: item.updatedAt?.toDate?.() || new Date(),
    isActive: item.isActive !== false
  };
}

export const announcementService = {
  // 공지사항 목록 가져오기 (관리자용) — 배열/V2 객체 듀얼 읽기
  async getAnnouncements(): Promise<Announcement[]> {
    try {
      const docRef = doc(db, ANNOUNCEMENT_COLLECTION, ANNOUNCEMENT_DOC_ID);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        const announcements: Announcement[] = [];

        if (data.content) {
          announcements.push({
            id: 'legacy',
            content: data.content,
            createdAt: data.createdAt?.toDate() || new Date(),
            updatedAt: data.updatedAt?.toDate() || new Date(),
            isActive: data.isActive !== false
          });
        }

        const list = announcementsToArray(data.announcements);
        list.forEach((item: any) => announcements.push(toAnnouncement(item)));

        return announcements.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
      }

      return [];
    } catch (error) {
      console.error('공지사항 목록 가져오기 실패:', error);
      return [];
    }
  },

  // 공지사항 생성 — V2: announcements.${id} 부분 업데이트
  async createAnnouncement(content: string, title?: string): Promise<void> {
    try {
      const docRef = doc(db, ANNOUNCEMENT_COLLECTION, ANNOUNCEMENT_DOC_ID);
      const docSnap = await getDoc(docRef);
      const id = `announcement-${Date.now()}`;
      const newAnnouncement: Record<string, unknown> = {
        id,
        content,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        isActive: true
      };
      if (title !== undefined && title !== null) {
        newAnnouncement.title = title;
      }

      if (!docSnap.exists()) {
        await setDoc(docRef, { announcements: { [id]: newAnnouncement } }, { merge: true });
        console.log('✅ 공지사항 생성 완료');
        return;
      }

      const data = docSnap.data();
      const raw = data.announcements;
      if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
        await updateDoc(docRef, { [`announcements.${id}`]: newAnnouncement });
      } else {
        const arr = announcementsToArray(raw);
        const obj: Record<string, any> = {};
        arr.forEach((item: any) => {
          const k = item.id || `announcement-${Date.now()}-${Math.random().toString(36).slice(2)}`;
          const base = {
            ...item,
            createdAt: item.createdAt?.toDate ? Timestamp.fromDate(item.createdAt.toDate()) : (item.createdAt || Timestamp.now()),
            updatedAt: item.updatedAt?.toDate ? Timestamp.fromDate(item.updatedAt.toDate()) : (item.updatedAt || Timestamp.now())
          };
          if (base.title === undefined) delete base.title;
          obj[k] = base;
        });
        obj[id] = newAnnouncement;
        await setDoc(docRef, { announcements: obj }, { merge: true });
      }
      console.log('✅ 공지사항 생성 완료');
    } catch (error) {
      console.error('공지사항 생성 실패:', error);
      throw error;
    }
  },

  // 공지사항 수정 — V2: 해당 id만 갱신
  async updateAnnouncement(id: string, content: string, title?: string): Promise<void> {
    try {
      const docRef = doc(db, ANNOUNCEMENT_COLLECTION, ANNOUNCEMENT_DOC_ID);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        throw new Error('공지사항을 찾을 수 없습니다.');
      }

      const data = docSnap.data();
      const raw = data.announcements;
      if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
        const existing = raw[id];
        if (!existing) {
          throw new Error('공지사항을 찾을 수 없습니다.');
        }
        const updated: Record<string, unknown> = {
          ...existing,
          content,
          updatedAt: Timestamp.now()
        };
        if (title !== undefined && title !== null) {
          updated.title = title;
        } else if (updated.title === undefined) {
          delete updated.title;
        }
        await updateDoc(docRef, { [`announcements.${id}`]: updated });
      } else {
        const arr = announcementsToArray(raw);
        const updated = arr.map((item: any) =>
          item.id === id ? { ...item, content, title, updatedAt: Timestamp.now() } : item
        );
        const obj: Record<string, any> = {};
        updated.forEach((item: any) => {
          const write = { ...item };
          if (write.title === undefined) delete write.title;
          obj[item.id] = write;
        });
        await setDoc(docRef, { announcements: obj }, { merge: true });
      }
      console.log('✅ 공지사항 수정 완료');
    } catch (error) {
      console.error('공지사항 수정 실패:', error);
      throw error;
    }
  },

  // 공지사항 삭제 — V2: deleteField(announcements.${id})
  async deleteAnnouncement(id: string): Promise<void> {
    try {
      const docRef = doc(db, ANNOUNCEMENT_COLLECTION, ANNOUNCEMENT_DOC_ID);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        throw new Error('공지사항을 찾을 수 없습니다.');
      }

      const data = docSnap.data();
      const raw = data.announcements;
      if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
        if (!(id in raw)) {
          throw new Error('공지사항을 찾을 수 없습니다.');
        }
        await updateDoc(docRef, { [`announcements.${id}`]: deleteField() });
      } else {
        const arr = announcementsToArray(raw).filter((item: any) => item.id !== id);
        const obj: Record<string, any> = {};
        arr.forEach((item: any) => {
          const write = { ...item };
          if (write.title === undefined) delete write.title;
          obj[item.id] = write;
        });
        await setDoc(docRef, { announcements: Object.keys(obj).length ? obj : {} }, { merge: true });
      }
      console.log('✅ 공지사항 삭제 완료');
    } catch (error) {
      console.error('공지사항 삭제 실패:', error);
      throw error;
    }
  },

  // 기존 단일 공지사항 가져오기 (하위 호환성)
  async getAnnouncement(): Promise<Announcement | null> {
    try {
      const docRef = doc(db, ANNOUNCEMENT_COLLECTION, ANNOUNCEMENT_DOC_ID);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          content: data.content || '',
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
          isActive: data.isActive !== false
        };
      }
      
      return null;
    } catch (error) {
      console.error('공지사항 가져오기 실패:', error);
      return null;
    }
  },

  // 공지사항 저장 (관리자용)
  async saveAnnouncement(content: string): Promise<void> {
    try {
      const docRef = doc(db, ANNOUNCEMENT_COLLECTION, ANNOUNCEMENT_DOC_ID);
      await setDoc(docRef, {
        content,
        updatedAt: Timestamp.now(),
        isActive: true
      }, { merge: true });
    } catch (error) {
      console.error('공지사항 저장 실패:', error);
      throw error;
    }
  }
};

